import { css } from '@emotion/css';
import {
  Card,
  ColorSwatch,
  Divider,
  Group,
  getThemeColor,
  Progress,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useId } from '@mantine/hooks';
import { i18nKeys, useTranslation } from 'locales';
import { formatPercentage } from 'modules/Analytics/components/charts/percentage';
import { useState } from 'react';
import { useLoadGroupInvoiceTotals } from 'shared/hooks/use-load-group-invoice-totals';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';

import { tooltipCardProps } from './tooltip-card';
import { useShareFormatter } from './use-share-formatter';

type GroupBalanceHeroProps = { selectedCompanies: ReadonlyArray<string> };

export const GroupBalanceHero = ({ selectedCompanies }: GroupBalanceHeroProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const titleId = useId();

  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);

  const formatter = useLocalizedCurrencyFormatter();
  const shareFormatter = useShareFormatter();

  const { groupInvoiceTotals, groupInvoiceTotalsError } = useLoadGroupInvoiceTotals({
    companies: selectedCompanies,
  });

  const composition = () => {
    if (!groupInvoiceTotals?.totalInvoices) return [];
    const { actionable, disputed, paused, totalInvoices, thirdParty, notDue } = groupInvoiceTotals;
    const remainder = totalInvoices - actionable - disputed - paused;
    return [
      {
        label: t(i18nKeys.ANALYTICS.TICKERS.ACTIONABLE.TITLE),
        amount: actionable,
        color: 'purple.6',
      },
      { label: t(i18nKeys.ANALYTICS.TICKERS.DISPUTED.TITLE), amount: disputed, color: 'warning.5' },
      { label: t(i18nKeys.ANALYTICS.TICKERS.PAUSED.TITLE), amount: paused, color: 'purple.5' },
      {
        label: t(i18nKeys.OTHER),
        amount: remainder,
        color: 'purple.3',
        isRemainder: true,
        breakdown: [
          { label: t(i18nKeys.ANALYTICS.OTHER_BREAKDOWN.THIRD_PARTY), amount: thirdParty },
          { label: t(i18nKeys.ANALYTICS.OTHER_BREAKDOWN.NOT_DUE), amount: notDue },
          {
            label: t(i18nKeys.ANALYTICS.OTHER_BREAKDOWN.IN_REMINDERS),
            amount: remainder - thirdParty - notDue,
          },
        ],
      },
    ].map(segment => ({ ...segment, share: segment.amount / totalInvoices }));
  };

  const amount = () => {
    if (groupInvoiceTotalsError) return '—';
    if (groupInvoiceTotals == null) return null;
    return formatter.format(groupInvoiceTotals.totalInvoices).replaceAll('\u202f', '\u00a0');
  };
  const figure = amount();
  const segments = composition();

  return (
    <Card
      component="section"
      aria-labelledby={titleId}
      data-tour="group-balance-hero"
      radius={16}
      pt="space7"
      px="space6"
      pb="space6"
      className={styles.card}
    >
      <Text id={titleId} size="xs" fw={600} tt="uppercase" c="dimmed">
        {t(i18nKeys.ANALYTICS.TICKERS.TOTAL.TITLE)}
      </Text>
      {figure == null ? (
        <Skeleton h={44} w={240} aria-busy />
      ) : (
        <Title order={1} fw={600} lh={1.05}>
          {figure}
        </Title>
      )}
      {figure == null && <Skeleton h={10} mt="lg" radius="xl" aria-busy />}
      <Progress.Root size={10} mt="lg" style={{ gap: 2, backgroundColor: 'transparent' }}>
        {segments.map(({ label, color, share, amount, breakdown }) => (
          <Tooltip
            key={label}
            label={
              <Stack gap={3}>
                <Group gap={7}>
                  <ColorSwatch color={getThemeColor(color, theme)} size={8} radius={2} />
                  <Text size="sm" fw={600} c="gray.7">
                    {label}
                  </Text>
                </Group>
                <Title order={3} fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatter.format(amount)}
                </Title>
                <Text size="sm" c="dimmed">
                  {t(i18nKeys.ANALYTICS.SHARE_OF_BALANCE, { share: shareFormatter.format(share) })}
                </Text>
                {breakdown && (
                  <>
                    <Divider color="gray.2" mt={8} mb={6} />
                    <Stack gap={6}>
                      {breakdown.map(part => (
                        <Group key={part.label} justify="space-between" gap={12}>
                          <Text size="sm" c="gray.7">
                            {part.label}
                          </Text>
                          <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatter.format(part.amount)}
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  </>
                )}
              </Stack>
            }
            position="top"
            opened={hoveredSegment === label}
            {...tooltipCardProps}
          >
            <Progress.Section
              value={share * 100}
              color={color}
              withAria={false}
              aria-label={label}
              onMouseEnter={() => setHoveredSegment(label)}
              onMouseLeave={() => setHoveredSegment(null)}
            />
          </Tooltip>
        ))}
      </Progress.Root>
      <Group component="ul" gap="xs" mt="sm" p={0} style={{ listStyle: 'none' }}>
        {segments.map(({ label, color, share, isRemainder }) => (
          <Group
            component="li"
            key={label}
            gap={6}
            className={styles.legendItem}
            aria-current={hoveredSegment === label}
            onMouseEnter={() => setHoveredSegment(label)}
            onMouseLeave={() => setHoveredSegment(null)}
          >
            <ColorSwatch color={getThemeColor(color, theme)} size={8} radius={2} />
            <Text size="sm" c={isRemainder ? 'dimmed' : undefined}>
              {`${label} ${formatPercentage(share)}`}
            </Text>
          </Group>
        ))}
      </Group>
    </Card>
  );
};

const styles = {
  card: css`
    background:
      radial-gradient(120% 130% at 0% 0%, var(--mantine-color-purple-1) 0%, transparent 60%),
      radial-gradient(120% 130% at 100% 10%, var(--mantine-color-coral-1) 0%, transparent 55%),
      var(--mantine-color-white);
  `,
  legendItem: css`
    padding: 2px 6px;
    border-radius: 6px;
    transition:
      transform 220ms cubic-bezier(0.2, 0.7, 0.2, 1),
      box-shadow 220ms cubic-bezier(0.2, 0.7, 0.2, 1);

    &[aria-current='true'] {
      transform: translateY(-2px);
      box-shadow: 0 6px 18px rgba(32, 10, 66, 0.06);
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
};
